import { Tooltip } from "antd";
import moment from "moment";

export const DEFAULT_GROUP_LABEL = "Uncategorized";
export const FORMULA_DATE_FORMAT = "MM-DD-YYYY";
export const FORMULA_DATE_INPUTS = [
  FORMULA_DATE_FORMAT,
  "MM/DD/YYYY",
  "M/D/YYYY",
  "YYYY-MM-DD",
];
export const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Normalizes arbitrary values into trimmed strings for consistent comparisons.
export function normalizeText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return "";
  return String(value).trim();
}

// Interprets boolean-like settings values returned by the backend.
export function isSettingEnabled(value) {
  const normalizedValue = normalizeText(value).toLowerCase();
  return (
    value === true ||
    value === 1 ||
    normalizedValue === "1" ||
    normalizedValue === "true"
  );
}

// Returns the first non-empty value from a list of possible fields.
export function firstFilled(...values) {
  for (const value of values) {
    const normalized = normalizeText(value);
    if (normalized) return normalized;
  }
  return "";
}

// Converts a label into a normalized lookup key for map access.
export function toLookupKey(value) {
  return firstFilled(value).toLowerCase();
}

// Builds the stable identifier used for procurement rows across the UI.
export function getProcurementRowIdentity(row, fallback = "") {
  return firstFilled(row?.entryID, row?.tempId, row?.id, fallback);
}

// Reads the parent reference that links a submittal to its family.
export function getProcurementParentReference(row) {
  return firstFilled(row?.parentID, row?.parentId);
}

// Resolves the top-level submittal that owns a nested family.
export function getProcurementFamilyRootId(row, rows = []) {
  const rowsById = new Map(
    (Array.isArray(rows) ? rows : [])
      .map((candidateRow, index) => [
        getProcurementRowIdentity(candidateRow, `row-${index}`),
        candidateRow,
      ])
      .filter(([rowId]) => Boolean(rowId)),
  );
  let currentRow = row;
  let currentRowId = getProcurementRowIdentity(row);
  const visitedRowIds = new Set();

  while (currentRowId && !visitedRowIds.has(currentRowId)) {
    visitedRowIds.add(currentRowId);

    const parentId = getProcurementParentReference(currentRow);

    if (!parentId) {
      return currentRowId;
    }

    const parentRow = rowsById.get(parentId);

    if (!parentRow) {
      return parentId;
    }

    currentRow = parentRow;
    currentRowId = getProcurementRowIdentity(parentRow, parentId);
  }

  return currentRowId;
}

// Extracts nesting and overwrite flags from the procurement settings payload.
export function getGroupingSettings(settingsInfo) {
  const resolvedSettings = Array.isArray(settingsInfo)
    ? settingsInfo[0]
    : settingsInfo;
  const groupingInfo = resolvedSettings?.groupingInfo || {};
  const nestSubmittals = isSettingEnabled(groupingInfo?.nestSubmittals);

  return {
    nestSubmittals,
    overwriteCalculations:
      nestSubmittals && isSettingEnabled(groupingInfo?.overwriteCalculations),
  };
}

// Combines optional codes and titles into a single display label.
export function buildDisplayLabel(code, title) {
  if (!code) return title;
  if (!title) return code;
  return title.startsWith(code) ? title : `${code} - ${title}`;
}

// Parses procurement dates using the supported formula input formats.
export function parseFormulaDate(value) {
  if (!value) return null;

  const strictParsed = moment(value, FORMULA_DATE_INPUTS, true);
  if (strictParsed.isValid()) {
    return strictParsed;
  }

  const fallbackParsed = moment(value);
  return fallbackParsed.isValid() ? fallbackParsed : null;
}

// Interprets backend working-day flags when building the shared business calendar.
export function isWorkingDayEnabled(value) {
  return value === "1" || value === 1 || value === true;
}

// Builds the holiday and weekend lookup used by date calculations across the procurement log.
export function buildBusinessCalendarContext(workingDays, nonWorkingDays) {
  const disabledWeekdays = new Set(
    WEEKDAY_NAMES.filter(
      (dayName) =>
        Object.prototype.hasOwnProperty.call(workingDays || {}, dayName) &&
        !isWorkingDayEnabled(workingDays?.[dayName]),
    ),
  );
  const disabledDates = new Set();

  (nonWorkingDays || []).forEach((holiday) => {
    const startDate = parseFormulaDate(holiday?.startDate)?.startOf("day");
    const finishDate = parseFormulaDate(holiday?.finishDate)?.startOf("day");

    if (!startDate || !finishDate) {
      return;
    }

    const rangeStart = startDate.isAfter(finishDate) ? finishDate : startDate;
    const rangeEnd = startDate.isAfter(finishDate) ? startDate : finishDate;
    const currentDate = rangeStart.clone();

    while (currentDate.isSameOrBefore(rangeEnd, "day")) {
      disabledDates.add(currentDate.format("YYYY-MM-DD"));
      currentDate.add(1, "day");
    }
  });

  return { disabledWeekdays, disabledDates };
}

// Checks whether a given date falls on a configured weekend or holiday.
export function isNonWorkingCalendarDay(date, businessCalendarContext) {
  if (!date?.isValid?.()) {
    return false;
  }

  const normalizedDate = date.clone().startOf("day");
  const weekdayName = WEEKDAY_NAMES[normalizedDate.day()];

  return Boolean(
    businessCalendarContext?.disabledWeekdays?.has(weekdayName) ||
      businessCalendarContext?.disabledDates?.has(
        normalizedDate.format("YYYY-MM-DD"),
      ),
  );
}

// Moves a date backward until it lands on the first available working day.
export function shiftMomentToPreviousWorkingDay(date, businessCalendarContext) {
  if (!date?.isValid?.()) {
    return null;
  }

  const normalizedDate = date.clone().startOf("day");

  if (
    !businessCalendarContext?.disabledWeekdays?.size &&
    !businessCalendarContext?.disabledDates?.size
  ) {
    return normalizedDate;
  }

  let previousWorkingDate = normalizedDate;
  let guard = 0;

  while (
    isNonWorkingCalendarDay(previousWorkingDate, businessCalendarContext) &&
    guard < 5000
  ) {
    previousWorkingDate = previousWorkingDate.clone().subtract(1, "day");
    guard += 1;
  }

  return previousWorkingDate;
}

// Moves a date forward until it lands on the next available working day.
export function shiftMomentToNextWorkingDay(date, businessCalendarContext) {
  if (!date?.isValid?.()) {
    return null;
  }

  const normalizedDate = date.clone().startOf("day");

  if (
    !businessCalendarContext?.disabledWeekdays?.size &&
    !businessCalendarContext?.disabledDates?.size
  ) {
    return normalizedDate;
  }

  let nextWorkingDate = normalizedDate;
  let guard = 0;

  while (
    isNonWorkingCalendarDay(nextWorkingDate, businessCalendarContext) &&
    guard < 5000
  ) {
    nextWorkingDate = nextWorkingDate.clone().add(1, "day");
    guard += 1;
  }

  return nextWorkingDate;
}

export function buildColumnPermissions(usersInfo) {
  const user = Array.isArray(usersInfo) ? usersInfo[0] : usersInfo;
  if (!user)
    return {
      accessLevel: 0,
      unlockedColumns: null,
      assignedOnly: false,
      assignedEntryIds: null,
      selectableEntryIds: null,
      hideSubRequiredDate: false,
    };

  const accessLevel = parseInt(user.accessLevel, 10) || 0;

  if (accessLevel > 1) {
    return {
      accessLevel,
      unlockedColumns: null,
      assignedOnly: false,
      assignedEntryIds: null,
      selectableEntryIds: null,
      hideSubRequiredDate: false,
    };
  }

  const granularMap = {
    leadTime: user.leadTimeGranularPermission,
    subCommittedDeliveryDate: user.subCommittedDeliveryDateGranularPermission,
    actualDeliveryDate: user.actualDeliveryDateGranularPermission,
  };

  const unlockedColumns = new Set(
    Object.entries(granularMap)
      .filter(([, permission]) => String(permission) === "3")
      .map(([field]) => field),
  );

  const viewOnlyAssigned =
    String(user.viewOnlyAssignedEntriesGranularPermission) !== "3";
  const selectableEntryIds = new Set(
    (user.taskIDsAssigned || []).flat().map(String),
  );
  const assignedEntryIds = viewOnlyAssigned
    ? selectableEntryIds
    : null;

  const hideSubRequiredDate =
    String(user.greyOutSubRequiredDateGranularPermission) === "3"; // 👈 add this

  return {
    accessLevel,
    unlockedColumns,
    assignedOnly: viewOnlyAssigned,
    assignedEntryIds,
    selectableEntryIds,
    hideSubRequiredDate,
  };
}

export const withPendingApprovalTooltip = (
  children,
  params,
  pendingApprovalIds,
) => {
  const isPendingApproval =
    params.data?.rowType === "submittal" &&
    pendingApprovalIds?.has(String(params.data?.entryID));

  if (!isPendingApproval) return children;

  return (
    <Tooltip title="Item is pending approval" placement="top" color="#1777ff">
      <span
        style={{
          display: "inline-block",
          width: "100%",
        }}
      >
        {children}
      </span>
    </Tooltip>
  );
};
